import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { useSessionStore } from '@/entities/session';
import { ROUTES } from '@/shared/config';
import {
  EVENT,
  getClientVersion,
  getPlatform,
  identifyPlayer,
  track,
  trackView,
  VIEW,
} from '@/shared/lib';
import type { ScreenName } from '@/shared/lib';

/**
 * Экраны и активации в Метрику (ТЗ заказчика на аналитику).
 *
 * Стоит ВНУТРИ защищённого корня: до входа игрока ещё нет, а привязывать
 * визит к игроку раньше опознания не к чему.
 *
 * Экраны уходят ПРОСМОТРАМИ, а не целями: в ТЗ у всех пяти стоит
 * «количество неуникальных заходов», а цели Метрика считает по визитам.
 * Разрез по экранам после этого читается в отчёте «Популярное» сам,
 * без настройки целей руками.
 *
 * Активации считать не нужно вовсе: неуникальные — это «Визиты»
 * Метрики, уникальные — «Посетители». Оба показателя она даёт сама,
 * как и DAU/WAU/MAU, ретеншен и время сессии.
 *
 * Ничего не рендерит: это наблюдатель, а не часть интерфейса.
 */
const SCREEN_BY_PATH: Record<string, ScreenName> = {
  [ROUTES.home]: 'home',
  [ROUTES.matches]: 'matches',
  [ROUTES.derby]: 'derby',
  [ROUTES.career]: 'career',
  [ROUTES.draws]: 'draws',
  [ROUTES.rating]: 'rating',
  [ROUTES.profile]: 'profile',
};

export function AnalyticsTracker() {
  const { pathname } = useLocation();
  const user = useSessionStore((state) => state.user);
  const isNewUser = useSessionStore((state) => state.isNewUser);
  const identified = useRef(false);

  useEffect(() => {
    if (!user || identified.current) return;
    identified.current = true;

    // Игрок в отчётах — это внутренний id. Ни телефона, ни ника,
    // ни telegram_id: последний связывает событие с живым аккаунтом.
    //
    // `acquisition_source` и подписка кладутся параметрами ПОСЕТИТЕЛЯ,
    // а не визита: ТЗ просит «распределение УНИКОВ по источникам»
    // и «по первому заходу подписан/нет», а уник в Метрике — посетитель
    // Клиент Telegram — не то же, что «операционная система» в отчётах
    // Метрики. Она видит только WebView, и Telegram Desktop у неё
    // неотличим от обычного браузера на том же Windows. Различие
    // практическое: другой размер экрана, нет безопасных зон, иначе
    // ведёт себя клавиатура — и жалоба на вёрстку без этого разреза
    // не воспроизводится
    identifyPlayer(user.id, {
      acquisition_source: user.acquisitionSource ?? 'direct',
      is_subscribed: user.isSubscribed,
      is_new_user: isNewUser,
      tg_platform: getPlatform(),
      tg_version: getClientVersion(),
    });
    track(EVENT.appOpened, { is_new_user: isNewUser });
  }, [user, isNewUser]);

  useEffect(() => {
    const screen = SCREEN_BY_PATH[pathname];
    // Неизвестный путь (например, 404) экраном не считается: в отчётах
    // нужны названные экраны из ТЗ, а не список опечаток в адресе
    if (!screen) return;
    trackView(VIEW.screen(screen));
  }, [pathname]);

  return null;
}
